import { jsx as _jsx } from "react/jsx-runtime";
import Modal from '@widget/popups/Modal';
import { Wizard } from './Wizard';
/**
 * This component is used to show a modal dialog that takes the user through a sequence of steps.
 * You define how each step should render.
 *
 * **V5 Implementation**: ModalWizard is now a thin wrapper around Modal + Wizard components.
 * It provides backward compatibility while leveraging the v5 architecture.
 *
 * @export
 *
 * @example
 * ```
 *  <ModalWizard
 *      show={show}
 *      title="Sample modal wizard"
 *      steps={[
 *      {
 *          id: "step-1",
 *          title: "Personal Details",
 *          render: (props) => <div>
 *              <FormField>
 *                  <Label>Name</Label>
 *                  <Input value={name} onChange={setName} />
 *              </FormField>
 *              <FormField>
 *                  <Label>Email</Label>
 *                  <Input value={email} onChange={setEmail} />
 *              </FormField>
 *          </div>,
 *          renderStatus: () => null, // Ignored in v5
 *          onValidateStep: () => "step-2"
 *      },
 *      {
 *          id: "step-2",
 *          title: "Educational Details",
 *          render: (props) => <div>
 *              <FormField>
 *                  <Label>University</Label>
 *                  <Input value={school} onChange={setSchool} />
 *              </FormField>
 *          </div>,
 *          renderStatus: () => null // Ignored in v5
 *      }
 *  ]}
 *  onClose={() => { setShow(false) }}
 *  onComplete={async () => { return executeAction("model", "action", {data: data}) }}
 * />
 * ```
 *
 */
const ModalWizard = (props) => {
    // Transform ModalWizard steps to Wizard steps
    const wizardSteps = props.steps.map((step, index) => {
        const stepId = step.id || `step-${index}`;
        const stepTitle = step.title || `Step ${index + 1}`;
        return {
            id: stepId,
            title: stepTitle,
            render: ({ next, prev }) => {
                // Wrap the original render to provide ModalWizard-style props
                const modalWizardProps = {
                    next: () => next(),
                    prev: prev,
                    currentStep: step,
                    data: undefined
                };
                return step.render(modalWizardProps);
            },
            onNext: () => {
                if (step.onValidateStep) {
                    const result = step.onValidateStep();
                    // Transform validation result to step ID
                    if (typeof result === 'string') {
                        return result; // Already a step ID
                    }
                    else if (typeof result === 'number') {
                        // Convert index to step ID
                        const targetStep = props.steps[result];
                        return (targetStep === null || targetStep === void 0 ? void 0 : targetStep.id) || targetStep ? `step-${result}` : null;
                    }
                    else if (typeof result === 'boolean') {
                        // true = continue, false = stay on current step
                        if (result) {
                            const nextIndex = index + 1;
                            const nextStep = props.steps[nextIndex];
                            return (nextStep === null || nextStep === void 0 ? void 0 : nextStep.id) || (nextStep ? `step-${nextIndex}` : null);
                        }
                        else {
                            return null; // Stay on current step
                        }
                    }
                    else if (result === null || result === undefined) {
                        return null; // Stay on current step
                    }
                }
                // Default: continue to next step
                const nextIndex = index + 1;
                const nextStep = props.steps[nextIndex];
                return (nextStep === null || nextStep === void 0 ? void 0 : nextStep.id) || (nextStep ? `step-${nextIndex}` : null);
            }
        };
    });
    return (_jsx(Modal, { show: props.show, onClose: props.onClose, title: props.title, className: `uxpcore_modalwizard ${props.className || ''}`, children: _jsx(Wizard, { steps: wizardSteps, onComplete: props.onComplete, completionTitle: props.completionText, showStepIndicator: true }) }));
};
export default ModalWizard;
